
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface TelegramGroup {
  id: string;
  chat_id: number;
  chat_title: string;
  is_active: boolean;
  created_at: string;
}

export function TelegramGroupManager() {
  const [groups, setGroups] = useState<TelegramGroup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('telegram_groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching groups:', error);
        toast({
          title: 'Error',
          description: `Failed to load Telegram groups: ${error.message}`,
          variant: 'destructive'
        });
        return;
      }

      setGroups(data || []);
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: `An unexpected error occurred: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (groupId: string, isActive: boolean) => {
    try {
      setIsUpdating(true);
      const { error } = await supabase
        .from('telegram_groups')
        .update({ is_active: !isActive })
        .eq('id', groupId);

      if (error) {
        console.error('Error updating group:', error);
        toast({
          title: 'Error',
          description: `Failed to update group: ${error.message}`,
          variant: 'destructive'
        });
        return;
      }

      // Update local state
      setGroups(groups.map(group => {
        if (group.id === groupId) {
          return { ...group, is_active: !isActive };
        }
        return group;
      }));

      toast({
        title: 'Success',
        description: `Group ${!isActive ? 'activated' : 'deactivated'} successfully`,
      });
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: `An unexpected error occurred: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupId) return;

    try {
      setIsUpdating(true);
      const { error } = await supabase
        .from('telegram_groups')
        .delete()
        .eq('id', selectedGroupId);

      if (error) {
        console.error('Error deleting group:', error);
        toast({
          title: 'Error',
          description: `Failed to delete group: ${error.message}`,
          variant: 'destructive'
        });
        return;
      }

      // Update local state
      setGroups(groups.filter(group => group.id !== selectedGroupId));
      setSelectedGroupId(null);

      toast({
        title: 'Success',
        description: `Group removed successfully`,
      });
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: `An unexpected error occurred: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telegram Group Management</CardTitle>
        <CardDescription>
          Manage Telegram groups that receive notifications from the system.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center p-8">
            <p className="text-muted-foreground">
              No Telegram groups are currently connected. Add your bot to a group and interact with it to register the group.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Group Name</TableHead>
                  <TableHead>Chat ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell className="font-medium">{group.chat_title || 'Unnamed Group'}</TableCell>
                    <TableCell>{group.chat_id}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id={`active-${group.id}`}
                          checked={group.is_active}
                          onCheckedChange={() => handleToggleActive(group.id, group.is_active)}
                          disabled={isUpdating}
                        />
                        <span className={group.is_active ? "text-green-600" : "text-gray-400"}>
                          {group.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-100"
                            onClick={() => setSelectedGroupId(group.id)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure you want to remove this group?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will stop all notifications being sent to this group. The bot will need to be added to the group again to reconnect it.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => setSelectedGroupId(null)}>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              className="bg-red-500 hover:bg-red-600"
                              onClick={handleDeleteGroup}
                              disabled={isUpdating}
                            >
                              {isUpdating ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Removing...
                                </>
                              ) : (
                                'Remove'
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
